import { formatSaved, savingsClaim } from '@nolens/domain';
import { SegmentedControl, useLocale } from '@nolens/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { QueryView } from '../components/Query';
import { inPeriod, PeriodSelect, SavingsTable, type Period } from '../components/Savings';
import { useWorkspaceSavings } from '../lib/queries';
import { spaceCrumbs, WithMe } from './workspace';
import { shellText } from '../i18n/shell';
import { spaceName } from '../lib/locale';
import { savingsText } from '../i18n/savings';

/**
 * Poupança estimada de um espaço, só para o dono desse espaço: por mês e por pessoa.
 * É a vista da própria empresa; o proprietário da aplicação só vê totais da plataforma.
 */
export function SavingsPage() {
  const { workspaceId } = useParams();
  const locale = useLocale();
  const t = savingsText(locale);
  const st = shellText(locale);
  return (
    <WithMe
      current={workspaceId}
      crumbs={(me) =>
        spaceCrumbs(
          me,
          workspaceId,
          locale,
          { label: st.stats, to: `/w/${workspaceId}/estatisticas` },
          { label: st.savings },
        )
      }
    >
      {(me) => {
        const ws = me.workspaces.find((w) => w.id === workspaceId);
        if (!ws) return <p className="text-text-2">{t.spaceNotFound}</p>;
        if (ws.role !== 'owner') return <p className="text-text-2">{t.ownerOnly}</p>;
        return <Savings ws={ws.id} name={spaceName(ws, locale)} />;
      }}
    </WithMe>
  );
}

function Savings({ ws, name }: { ws: string; name: string }) {
  const locale = useLocale();
  const t = savingsText(locale);
  const q = useWorkspaceSavings(ws);
  const [by, setBy] = useState<'month' | 'person'>('month');
  const [period, setPeriod] = useState<Period>('all');
  return (
    <section>
      <p className="text-[12px] font-semibold uppercase tracking-wide text-text-3">{name}</p>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t.title}</h1>
      <QueryView q={q}>
        {(s) => (
          <>
            <p className="mt-3 flex flex-wrap items-baseline gap-x-3" data-workspace-savings>
              <span className="text-[30px] font-semibold tracking-tight text-accent-text">
                ≈ {formatSaved(s.savedSeconds, locale)}
              </span>
              <span className="text-text-2">{t.summary(s.steps, s.guides)}</span>
            </p>
            <p className="mt-1 text-[12.5px] text-text-3">
              {savingsClaim(locale)} {t.notAMeasurement}
            </p>
            <div className="mt-6 flex flex-wrap items-end gap-3">
              <SegmentedControl
                label={t.viewBy}
                value={by}
                onChange={setBy}
                options={[
                  { value: 'month', label: t.month },
                  { value: 'person', label: t.person },
                ]}
              />
              {by === 'month' && <PeriodSelect value={period} onChange={setPeriod} />}
            </div>
            {by === 'month' ? (
              <SavingsTable
                first={t.month}
                months
                rows={s.byMonth
                  .filter((m) => inPeriod(m.month, period))
                  .map((m) => ({ key: m.month, label: m.month, guides: m.guides, steps: m.steps }))}
              />
            ) : (
              <SavingsTable
                first={t.person}
                rows={s.byMember.map((m) => ({
                  key: m.userId ?? '—',
                  label: m.email ?? t.formerMember,
                  guides: m.guides,
                  steps: m.steps,
                }))}
              />
            )}
          </>
        )}
      </QueryView>
    </section>
  );
}
